import React from "react";
import { StoreSearchFilters } from "tsi-common-react/src/apps/retail/models";
import { IRetailStoreWithDistance } from "tsi-common-react/src/models/location";
import { unique } from "tsi-common-react/src/utils/functional";

import { FilterChangeHandler } from "../models";
import { LocatorFilterPopover } from "./LocatorFilters_Popover";

interface IProps {
    filterValues: StoreSearchFilters;
    stores: ReadonlyArray<IRetailStoreWithDistance>;
    onChange: FilterChangeHandler;
}

const getStoreNames = (
    stores: ReadonlyArray<IRetailStoreWithDistance>,
): string[] => {
    return stores
        .map((s) => s.name)
        .filter(unique)
        .sort();
};

const buildInputs = (
    storeNames: string[],
    selectedStoreNames: Set<string>,
    onInputChange: (event: React.FormEvent<HTMLInputElement>) => void,
): React.ReactNode[] => {
    return storeNames.map((name) => {
        return (
            <label key={name} className="filters__list-item">
                <input
                    type="checkbox"
                    name="retailer"
                    value={name}
                    onChange={onInputChange}
                    checked={selectedStoreNames.has(name)}
                />{" "}
                {name}
            </label>
        );
    });
};

export const RetailerFilter = (props: IProps): React.ReactNode => {
    const [selectedStoreNames, setSelectedStoreNames] = React.useState<
        Set<string>
    >(new Set(props.filterValues.retailerName));

    const filterElem = React.useRef<{
        onRequestClose: () => void;
    }>(null);

    // Handle getDerivedStateFromProps logic: sync selectedStoreNames with Redux filter values
    const prevFilterValueRef = React.useRef<readonly string[]>(
        props.filterValues.retailerName,
    );
    React.useEffect(() => {
        if (props.filterValues.retailerName !== prevFilterValueRef.current) {
            prevFilterValueRef.current = props.filterValues.retailerName;
            setSelectedStoreNames(new Set(props.filterValues.retailerName));
        }
    }, [props.filterValues.retailerName]);

    const onInputChange = (event: React.FormEvent<HTMLInputElement>) => {
        const checked = event.currentTarget.checked;
        const value = event.currentTarget.value;
        setSelectedStoreNames((prev) => {
            const updated = new Set(prev);
            if (checked) {
                updated.add(value);
            } else {
                updated.delete(value);
            }
            return updated;
        });
    };

    const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        props.onChange({
            retailerName: Array.from(selectedStoreNames),
        });
        if (filterElem.current) {
            filterElem.current.onRequestClose();
        }
    };

    const onReset = () => {
        // Deselect everything
        props.onChange({
            retailerName: [],
        });
        if (filterElem.current) {
            filterElem.current.onRequestClose();
        }
    };

    const onCancel = () => {
        // Close the modal
        if (filterElem.current) {
            filterElem.current.onRequestClose();
        }
    };

    const onAfterClose = () => {
        // Revert filter back to the state in Redux
        setSelectedStoreNames(new Set(prevFilterValueRef.current));
    };

    const storeNames = getStoreNames(props.stores);

    const inputs = buildInputs(storeNames, selectedStoreNames, onInputChange);

    return (
        <LocatorFilterPopover
            ref={filterElem}
            id="retailers"
            // TODO(i18n): Use t tagged template: labelText={t`retailers`}
            labelText="retailers"
            onAfterClose={onAfterClose}
        >
            <form
                className="filters__dropdown filters__dropdown--retailers"
                onSubmit={onSubmit}
            >
                <fieldset>
                    {/* TODO(i18n): Use t tagged template: <legend className="filters__instruct">{t`Narrow your search results by selecting the retailers that interest you.`}</legend> */}
                    <legend className="filters__instruct">
                        Narrow your search results by selecting the retailers
                        that interest you.
                    </legend>
                    <h3 className="filters__header">
                        {/* TODO(i18n): Use t tagged template: {t`Retailers`} */}
                        Retailers{" "}
                        {/* TODO(i18n): Use t tagged template: <span className="filters__header--right">{t`A-Z`}</span> */}
                        <span className="filters__header--right">A-Z</span>
                    </h3>
                    <div className="filters__list filters__list--scrollable bh-sl-filters">
                        {inputs}
                    </div>
                    <div className="filters__actions filters__actions--full-width">
                        <label
                            htmlFor="retailer-apply-button"
                            style={{ display: "none" }}
                        >
                            {/* TODO(i18n): Use t tagged template: {t`Apply Selections`} */}
                            Apply Selections
                        </label>
                        <input
                            id="retailer-apply-button"
                            className="filters__button button"
                            type="submit"
                            // TODO(i18n): Use t tagged template for value attribute: value={t`Apply Selections`}
                            value="Apply Selections"
                        />
                        <p className="filters__links">
                            <a
                                className="filters__link close-reset"
                                onClick={onReset}
                            >
                                {/* TODO(i18n): Use t tagged template: {t`Reset`} */}
                                Reset
                            </a>
                            <a
                                className="filters__link close-tooltip"
                                onClick={onCancel}
                            >
                                {/* TODO(i18n): Use t tagged template: {t`Cancel`} */}
                                Cancel
                            </a>
                        </p>
                    </div>
                </fieldset>
            </form>
        </LocatorFilterPopover>
    );
};
